import { Monitor, Smartphone } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';

interface AdPreviewProps {
  title: string;
  description: string;
  imageUrl: string;
  position: string;
}

export const AdPreview = ({ title, description, imageUrl, position }: AdPreviewProps) => {
  const renderAdContent = (isMobile: boolean) => {
    const hasImage = !!imageUrl;
    
    // Banner positions (top, hero)
    if (position === 'top' || position === 'hero') {
      return (
        <div className={`relative overflow-hidden rounded-lg ${isMobile ? 'h-32' : 'h-48'} bg-gradient-to-r from-primary/20 to-primary/10`}>
          {hasImage ? (
            <img 
              src={imageUrl} 
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`h-full flex flex-col justify-center ${isMobile ? 'px-4 py-2' : 'px-8 py-4'}`}>
              <h3 className={`font-bold ${isMobile ? 'text-sm' : 'text-2xl'} line-clamp-1`}>
                {title || 'Titre de la publicité'}
              </h3>
              {description && (
                <p className={`${isMobile ? 'text-xs mt-1' : 'text-base mt-2'} line-clamp-2`}>
                  {description}
                </p>
              )}
            </div>
          )}
        </div>
      );
    }

    // Sidebar position
    if (position === 'sidebar') {
      return (
        <div className="rounded-lg border bg-card overflow-hidden">
          {hasImage && (
            <img 
              src={imageUrl} 
              alt={title}
              className={`w-full ${isMobile ? 'h-32' : 'h-48'} object-cover`}
            />
          )}
          <div className={`${isMobile ? 'p-2' : 'p-4'}`}>
            <h4 className={`font-semibold ${isMobile ? 'text-xs' : 'text-sm'} line-clamp-2`}>
              {title || 'Titre de la publicité'}
            </h4>
            {description && (
              <p className={`text-muted-foreground ${isMobile ? 'text-xs mt-1' : 'text-xs mt-2'} line-clamp-3`}>
                {description}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Middle and Bottom positions
    return (
      <div className="rounded-lg border bg-card overflow-hidden">
        {hasImage ? (
          <img 
            src={imageUrl} 
            alt={title}
            className={`w-full ${isMobile ? 'h-40' : 'h-56'} object-cover`}
          />
        ) : (
          <div className={`${isMobile ? 'p-3' : 'p-6'}`}>
            <h3 className={`font-bold ${isMobile ? 'text-base' : 'text-xl'}`}>
              {title || 'Titre de la publicité'}
            </h3>
            {description && (
              <p className={`text-muted-foreground ${isMobile ? 'text-sm mt-2' : 'text-base mt-3'} line-clamp-3`}>
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Monitor className="h-4 w-4" />
        Aperçu en temps réel
      </h3>
      
      <Tabs defaultValue="desktop" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="desktop">
            <Monitor className="h-4 w-4 mr-2" />
            Desktop
          </TabsTrigger>
          <TabsTrigger value="mobile">
            <Smartphone className="h-4 w-4 mr-2" />
            Mobile
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="desktop" className="mt-4">
          <div className="border rounded-lg p-6 bg-background">
            <div className={position === 'sidebar' ? 'max-w-xs' : 'w-full'}>
              {renderAdContent(false)}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Aperçu desktop - Position: {position}
          </p>
        </TabsContent>
        
        <TabsContent value="mobile" className="mt-4">
          <div className="border rounded-lg p-4 bg-background max-w-sm mx-auto">
            {renderAdContent(true)}
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Aperçu mobile - Position: {position}
          </p>
        </TabsContent>
      </Tabs>
    </Card>
  );
};
